import React, { useMemo, useState } from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Eye } from "lucide-react";
import { usePaymentList, useAllAccounts } from '../../../../../shared/hook';
import { getRemoteUtilityId } from '@shared/utils/getUtilityId';
import { getLoginDataFromStorage } from '@shared/utils/loginUtils';
import { useCurrency } from '@shared/selectors/globalSelectors';
import { Dialog, DialogContent } from '@shared/ui/dialog';
import { PaymentDetailsModal } from '../../../../../bills/components/tabs/PaymentHistory';
import { useTranslation } from 'react-i18next';

export default function PaymentHistory() {
  const { t } = useTranslation();
  const [selectedPayment, setSelectedPayment] = useState<any>(null);
  const [page, setPage] = useState(1);
  const limit = 20;
  const currency = useCurrency();
  
  const { consumerId } = getLoginDataFromStorage();
  const remoteUtilityId = getRemoteUtilityId();

  const { data: allAccounts } = useAllAccounts({
    page: 1,
    limit: 1000,
    active_consumers: 1,
    remote_utility_id: remoteUtilityId,
    is_sub_account: 1,
    consumer_id: getLoginDataFromStorage().consumerId,
  });

  const allConsumerIds = useMemo(() => {
    const subIds = allAccounts?.results?.map((acc: any) => acc.id) || [];
    return [subIds];
  }, [allAccounts, consumerId]);

  const { data: paymentsData, isLoading } = usePaymentList({
    remote_utility_id: remoteUtilityId,
    consumer_id: allConsumerIds,
    page,
    limit
  });

  console.log(allConsumerIds,'allConsumerIds')

  const paymentRecords = paymentsData?.results || [];
  const totalCount = paymentsData?.count || paymentsData?.result?.count || 0;
  const totalPages = Math.ceil(totalCount / limit) || 1;
  const startItem = totalCount > 0 ? (page - 1) * limit + 1 : 0;
  const endItem = Math.min(page * limit, totalCount);

  const historyColumns: TableColumn<any>[] = useMemo(() => [
    {
      key: "consumer",
      header: t("Account"),
      render: (val, row) => (
        <div className="flex items-center space-x-3">
          <div className="w-2 h-2 rounded-full bg-blue-600"></div>
          <div className="flex flex-col">
            <span className="text-sm font-medium text-gray-900">{row?.consumer?.consumerNo}</span>
            <span className="text-xs text-gray-500">{row?.consumer?.consumerName}</span>
          </div>
        </div>
      )
    },
    {
      key: "transactionId",
      header: t("Receipt Id"),
      render: (val, row) => <span className="font-semibold text-xs text-gray-800">{val || row.id}</span>
    },
    {
      key: "remoteBillId",
      header: t("Bill Id"),
      render: (val) => <span className="text-xs text-gray-600">{val || t("N/A")}</span>
    },
    {
      key: "amount",
      header: t("Amount Paid"),
      render: (val) => <span className="font-bold text-sm text-green-700">{currency} {Number(val || 0).toFixed(2)}</span>
    },
    {
      key: "paymentDate",
      header: t("Paid Date"),
      render: (val) => <span className="text-xs text-gray-600">{val || t("N/A")}</span>
    },
    {
      key: "paymentMode",
      header: t("Method"),
      render: (val) => {
        const isAuto = val === "Auto-Paid";
        return (
          <span className={`text-xs ${isAuto ? 'text-green-600 font-medium' : 'text-gray-500'}`}>
            {val || 'Online'}
          </span>
        );
      }
    },
    {
      key: "paymentReceivedStatus",
      header: t("Status"),
      render: (val) => {
        let color = "text-gray-600";
        if (val === 'Posted') color = "text-green-600 font-semibold";
        if (val === 'Failed') color = "text-red-600 font-semibold";
        if (val === 'In Progress') color = "text-orange-600 font-semibold";
        return <span className={`text-xs ${color}`}>{val || t("N/A")}</span>;
      }
    },
    {
      key: "actions",
      header: t("Actions"),
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setSelectedPayment(row)} className="h-7 px-2.5 text-xs rounded-md font-semibold flex items-center gap-1 border-gray-200 text-gray-700">
             <Eye className="w-3.5 h-3.5"/> {t("View")}
          </Button>
        </div>
      )
    }
  ], [currency, t]);

  return (
    <div className="mt-4">
      
      <div>
        <GenericTable
          data={paymentRecords}
          columns={historyColumns}
          loading={isLoading || !allConsumerIds}
          pagination={{
            currentPage: page,
            totalPages: totalPages,
            startItem: startItem,
            endItem: endItem,
            totalItems: totalCount
          }}
          onPageChange={setPage}
          rowKey="id"
          selectable={false}
          searchPlaceholder={t("Search by receipt ID or bill ID...")}
          onSearch={() => {}}
          filters={
            <select className="border border-gray-300 rounded-md px-4 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 h-9">
              <option>{t("All Accounts")}</option>
            </select>
          }
        />
      </div>

      <Dialog open={!!selectedPayment} onOpenChange={(open) => !open && setSelectedPayment(null)}>
        <DialogContent className="sm:max-w-xl p-0 border-none bg-white shadow-xl rounded-xl">
          <PaymentDetailsModal payment={selectedPayment} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
